import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import {
  DisplayCurrency,
  normalizeDisplayCurrency,
} from "../utils/displayCurrency";

interface SelectedAsset {
  asset?: string;
  [key: string]: unknown;
}

interface CurrencySelectionContextValue {
  selectedAsset: SelectedAsset | null;
  setSelectedAsset: React.Dispatch<React.SetStateAction<SelectedAsset | null>>;
  selectedCurrency: DisplayCurrency;
  setSelectedCurrency: React.Dispatch<React.SetStateAction<DisplayCurrency>>;
  isCrypto: boolean;
}

interface CurrencySelectionHookValue extends CurrencySelectionContextValue {
  isInProvider: boolean;
}

const CurrencySelectionContext =
  createContext<CurrencySelectionContextValue | null>(null);

interface CurrencySelectionProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = "80eight.displayCurrency";
export const DISPLAY_CURRENCY_CHANGE_EVENT = "80eight.displayCurrencyChanged";

function getStoredDisplayCurrency(): DisplayCurrency {
  if (typeof window === "undefined") {
    return "ZAR";
  }

  return normalizeDisplayCurrency(window.sessionStorage.getItem(STORAGE_KEY));
}

function persistDisplayCurrency(currency: DisplayCurrency): void {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(STORAGE_KEY, currency);
  window.dispatchEvent(
    new CustomEvent(DISPLAY_CURRENCY_CHANGE_EVENT, { detail: currency })
  );
}

export const CurrencySelectionProvider: React.FC<
  CurrencySelectionProviderProps
> = ({ children }) => {
  const [selectedAsset, setSelectedAsset] = useState<SelectedAsset | null>(
    null
  );
  const [selectedCurrencyState, setSelectedCurrencyState] =
    useState<DisplayCurrency>(() => getStoredDisplayCurrency());

  const setSelectedCurrency = useCallback<
    React.Dispatch<React.SetStateAction<DisplayCurrency>>
  >((value) => {
    setSelectedCurrencyState((current) => {
      const nextValue =
        typeof value === "function"
          ? value(current)
          : normalizeDisplayCurrency(value);
      persistDisplayCurrency(nextValue);
      return nextValue;
    });
  }, []);

  useEffect(() => {
    const handleDisplayCurrencyChange = (event: Event) => {
      const nextCurrency =
        event instanceof CustomEvent
          ? normalizeDisplayCurrency(event.detail)
          : getStoredDisplayCurrency();

      setSelectedCurrencyState(nextCurrency);
    };

    window.addEventListener(
      DISPLAY_CURRENCY_CHANGE_EVENT,
      handleDisplayCurrencyChange
    );
    window.addEventListener("storage", handleDisplayCurrencyChange);

    return () => {
      window.removeEventListener(
        DISPLAY_CURRENCY_CHANGE_EVENT,
        handleDisplayCurrencyChange
      );
      window.removeEventListener("storage", handleDisplayCurrencyChange);
    };
  }, []);

  const setSelectedAssetValue = useCallback<
    React.Dispatch<React.SetStateAction<SelectedAsset | null>>
  >((value) => {
    setSelectedAsset((current) =>
      typeof value === "function" ? value(current) : value
    );
  }, []);

  const selectedCurrency = selectedCurrencyState;
  const selectedAssetCode = selectedAsset?.asset?.toUpperCase();
  const isCrypto = selectedAssetCode
    ? selectedAssetCode !== "ZAR"
    : selectedCurrency !== "ZAR";

  const value: CurrencySelectionContextValue = useMemo(
    () => ({
      selectedAsset,
      setSelectedAsset: setSelectedAssetValue,
      selectedCurrency,
      setSelectedCurrency,
      isCrypto,
    }),
    [
      selectedAsset,
      selectedCurrency,
      setSelectedAssetValue,
      setSelectedCurrency,
      isCrypto,
    ]
  );

  return (
    <CurrencySelectionContext.Provider value={value}>
      {children}
    </CurrencySelectionContext.Provider>
  );
};

export const useCurrencySelection = (): CurrencySelectionHookValue => {
  const context = useContext(CurrencySelectionContext);
  if (!context) {
    return {
      selectedAsset: null,
      setSelectedAsset: () => {},
      selectedCurrency: "ZAR",
      setSelectedCurrency: () => {},
      isCrypto: false,
      isInProvider: false,
    };
  }
  return { ...context, isInProvider: true };
};
